/**
 * Wave 3.71 — Change Karigar (goldsmith) on an order.
 *
 * Why a dedicated dialog?
 * ----------------------
 * The operation is destructive-adjacent — it CANCELS the current order
 * and creates a brand-new one under a new order number. Even though the
 * backend rebuilds the artefact perfectly (images, comments, milestones,
 * requirements all carry over) we want the operator to explicitly
 * confirm the trade-off before proceeding.
 *
 * Preconditions enforced by the caller:
 *   - `order.has_material_issues` must be false. The pencil affordance
 *     is hidden the moment the flag flips true.
 *   - Caller has already verified the user is Company Admin or Super
 *     Admin (backend re-checks anyway).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { AlertTriangle, ArrowRightLeft, Loader2 } from 'lucide-react';

export function ChangeKarigarDialog({
  open,
  onOpenChange,
  order,
  companyId,
  onShifted,        // (newOrder) => void — parent navigates to new order id
}) {
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Wave 4.x — What happens to the ORIGINAL order after the shift:
  // 'keep' (mark Cancelled, default) or 'delete' (permanently remove).
  const [oldOrderAction, setOldOrderAction] = useState('keep');

  // Reset local state whenever the dialog is (re-)opened.
  useEffect(() => {
    if (open) {
      setSelectedId('');
      setReason('');
      setAck(false);
      setSubmitting(false);
      setOldOrderAction('keep');
    }
  }, [open]);

  // Fetch the goldsmith list scoped to the same company as the order.
  // We filter out the current karigar so the operator can only pick a
  // different one (the backend also rejects same-karigar picks, but
  // hiding it up-front avoids a wasted round-trip).
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/masters/goldsmiths', {
          params: { company_id: companyId },
        });
        if (cancelled) return;
        setGoldsmiths(Array.isArray(data) ? data : []);
      } catch (_e) {
        if (!cancelled) setGoldsmiths([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  const pickable = useMemo(
    () => goldsmiths.filter((g) => g.id !== order?.goldsmith_id),
    [goldsmiths, order?.goldsmith_id],
  );

  const submit = async () => {
    if (!selectedId) {
      toast.error('Pick a new Karigar first.');
      return;
    }
    if (!ack) {
      toast.error('Please confirm you understand this cancels the current order.');
      return;
    }
    setSubmitting(true);
    try {
      const deleteOld = oldOrderAction === 'delete';
      const { data } = await api.post(
        `/orders/${order.id}/change-karigar`,
        {
          new_goldsmith_id: selectedId,
          reason: reason.trim() || undefined,
          delete_old_order: deleteOld,
        },
      );
      const newOrder = data?.new_order || null;
      toast.success(
        deleteOld
          ? `Order shifted. New order ${newOrder?.order_no || ''} created; ${order.order_no} permanently deleted.`
          : `Order shifted. New order ${newOrder?.order_no || ''} created; ${order.order_no} cancelled.`,
      );
      if (onShifted) onShifted(newOrder);
    } catch (e) {
      toast.error(extractError(e, 'Karigar shift failed'));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="change-karigar-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-[var(--ink-navy)]" />
            Change Karigar
          </DialogTitle>
          <DialogDescription>
            Reassigning the Karigar on this order will <strong>cancel</strong>{' '}
            <span className="font-mono">{order?.order_no}</span> and create a fresh
            order under a new order number. Title, description, customer,
            images, comments, milestones and every requirement are carried
            over exactly — only the Karigar changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current karigar (read-only reference) */}
          <div className="rounded-md border border-[var(--divider)] bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">
              Current Karigar
            </div>
            <div className="text-sm font-medium text-[var(--ink-navy)]">
              {order?.goldsmith_name || '—'}
            </div>
          </div>

          {/* New karigar picker */}
          <div>
            <Label className="text-[12px]">
              New Karigar <span className="text-rose-500">*</span>
            </Label>
            <SearchableSelect
              items={pickable}
              value={selectedId}
              onValueChange={(v) => setSelectedId(v || '')}
              itemToLabel={(g) => g.name}
              searchKeys={['name', 'phone', 'email']}
              placeholder={
                pickable.length === 0
                  ? 'No other goldsmiths available in this company'
                  : 'Pick a Karigar'
              }
              searchPlaceholder="Search Karigar…"
              disabled={pickable.length === 0}
              testId="change-karigar-goldsmith-select"
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Only Karigars from the same company are listed.
            </p>
          </div>

          {/* Optional reason */}
          <div>
            <Label className="text-[12px]">Reason (optional)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Karigar unavailable, quality issue, customer request…"
              data-testid="change-karigar-reason"
              maxLength={500}
            />
          </div>

          {/* Wave 4.x — What happens to the current order after the shift */}
          <div>
            <Label className="text-[12px]">
              After shifting, the current order form{' '}
              <span className="font-mono">{order?.order_no}</span> should be:
            </Label>
            <RadioGroup
              value={oldOrderAction}
              onValueChange={setOldOrderAction}
              className="mt-2 space-y-2"
              data-testid="change-karigar-old-order-action"
            >
              <label
                htmlFor="ck-keep"
                className={`flex items-start gap-2.5 rounded-md border p-2.5 cursor-pointer transition-colors ${
                  oldOrderAction === 'keep'
                    ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)]/5'
                    : 'border-[var(--divider)] hover:bg-slate-50'
                }`}
              >
                <RadioGroupItem value="keep" id="ck-keep" className="mt-0.5" data-testid="change-karigar-keep-old" />
                <div>
                  <div className="text-sm font-medium text-[var(--ink-navy)]">Keep it in the system</div>
                  <div className="text-[11px] text-slate-500">
                    The old order is marked <strong>Cancelled</strong> and stays for audit, linked to the new order.
                  </div>
                </div>
              </label>
              <label
                htmlFor="ck-delete"
                className={`flex items-start gap-2.5 rounded-md border p-2.5 cursor-pointer transition-colors ${
                  oldOrderAction === 'delete'
                    ? 'border-rose-400 bg-rose-50'
                    : 'border-[var(--divider)] hover:bg-slate-50'
                }`}
              >
                <RadioGroupItem value="delete" id="ck-delete" className="mt-0.5" data-testid="change-karigar-delete-old" />
                <div>
                  <div className="text-sm font-medium text-rose-700">Delete it permanently</div>
                  <div className="text-[11px] text-slate-500">
                    The old order and its comments/history are removed for good. This cannot be undone.
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Warning + acknowledgement */}
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex gap-2 items-start">
            <AlertTriangle size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-900 leading-relaxed">
              This action is <strong>not reversible</strong>. A new order form will be created with a
              new number, and this order will be{' '}
              <strong>{oldOrderAction === 'delete' ? 'permanently deleted' : 'marked Cancelled'}</strong>.
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox
              checked={ack}
              onCheckedChange={(v) => setAck(!!v)}
              data-testid="change-karigar-ack"
              className="mt-0.5"
            />
            <span>
              I understand this will create a new order under a new number and{' '}
              {oldOrderAction === 'delete' ? (
                <><strong>permanently delete</strong> <span className="font-mono">{order?.order_no}</span></>
              ) : (
                <>cancel <span className="font-mono">{order?.order_no}</span></>
              )}.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedId || !ack}
            className="luxury-button"
            data-testid="change-karigar-submit"
          >
            {submitting ? (
              <><Loader2 size={14} className="mr-1.5 animate-spin" /> Shifting…</>
            ) : (
              <><ArrowRightLeft size={14} className="mr-1.5" /> Cancel &amp; Recreate</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChangeKarigarDialog;
